import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../api/analytics';
import AssessmentDemo from './AssessmentDemo';
import TestRunner from './TestRunner';
import TestResult from './TestResult';
import ProgressView from './ProgressView';
import ArticleView from './ArticleView';
import { articleImage, articleHasArt } from './articleArt';
import { badgeImage } from './badgeArt';
import { avatarImage } from './avatarArt';
import PlacementQuiz from './PlacementQuiz';
import WrongQuestionBook from './WrongQuestionBook';
import MockExamList from './MockExamList';
import ScoreReport from './ScoreReport';
import { getPlacementReport, getPracticeProgress, getEngagement, getLeaderboard, PlacementResult, PracticeProgress, EngagementData, LeaderboardData } from '../api/placement';
import WeeklyGoalPicker from './WeeklyGoalPicker';
import { useConfirm } from './quiz/ConfirmDialog';
import { DashboardData } from '../api/ocPracticeApi';
import {
  getRecommendedTests,
  RecommendedTestsResponse,
  EXAM_META,
} from '../api/onboarding';
import logoUrl from '../assets/images/cleveroo-logo.png';
import './StudentDashboard.css';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────
interface EnrolledClass {
  id: string;
  name: string;
  description: string;
  grade: string;
  academic_year: string;
  teacher_name: string;
  teacher_email: string;
  total_students: number;
  created_at: string;
}

interface PendingTest {
  test_id: string;
  title: string;
  description: string;
  subject: string;
  difficulty: string;
  total_marks: number;
  estimated_time: number;
  class_name: string;
  due_date: string | null;
  instructions: string;
  max_attempts: number;
  assigned_at: string;
  attempts_used: number;
  student_status: 'not_started' | 'in_progress' | 'completed';
}

type SubjectTone = 'green' | 'orange' | 'grape' | 'coral';
type DueVariant = 'today' | 'soon' | 'later';

interface SubjectProgressVM {
  id: string;
  name: string;
  delta: string; // "+6%" or "Started Tue"
  pct: number; // 0..100
  tone: SubjectTone;
}

interface QuestStepVM {
  id: string;
  title: string;
  meta: string;
  time: string; // "✓ Done" / "~7 min"
  done: boolean;
}

interface PendingVM {
  id: string;
  letter: string; // M, T, R…
  tone: SubjectTone; // colors the ico
  title: string;
  meta: string;
  due: string;
  dueVariant: DueVariant;
}

interface QATile {
  id: string;
  name: string;
  count: string; // "12" or "2"
  unit: string; // "tests ready"
  tone: SubjectTone;
}

interface UpcomingVM {
  id: string;
  day: number | string;
  weekday: string;
  title: string;
  meta: string;
  initials: string;
  tone: 1 | 2 | 3;
  joinUrl?: string | null;
}

interface ResourceVM {
  id: string;
  title: string;
  type: string;
  subject: string;
  url: string;
  description: string;
  category?: string | null;
  slug?: string | null;
  readMinutes?: number | null;
  isArticle?: boolean;
}

interface AchievementVM {
  id: string;
  name: string;
  prog: string;
  unlocked: boolean;
  tone?: 'coral' | 'grape' | 'green' | 'yellow';
  glyph: string; // emoji / ★
}

interface ActivityVM {
  id: string;
  text: React.ReactNode; // allows <b>
  sub: string;
  score?: string;
  scoreVariant?: 'green' | 'coral' | 'orange';
  timeOnly?: string;
  tone: 'green' | 'coral' | 'grape' | 'orange';
}

interface FeedbackVM {
  id: string;
  initials: string;
  name: string;
  when: string;
  body: React.ReactNode;
}

// ────────────────────────────────────────────────────────────────────────────
// Inline SVG icons (copied verbatim from handoff)
// ────────────────────────────────────────────────────────────────────────────
const Icon = {
  Home: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z"/></svg>
  ),
  Practice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9h12l-1 11H7z"/><path d="M9 9V7a3 3 0 1 1 6 0v2"/></svg>
  ),
  Programs: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V5a2 2 0 0 1 2-2h11l3 3v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"/><path d="M8 8h6m-6 4h8m-8 4h5"/></svg>
  ),
  Resources: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 19V7a2 2 0 0 1 2-2h11l4 4v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M8 11h8m-8 4h6"/></svg>
  ),
  Flame: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c0 4-3 5-3 8a3 3 0 1 0 6 0c0-1.5-1-2-1-4 2 1 4 3 4 7a6 6 0 1 1-12 0c0-4 3-5 3-8z"/></svg>
  ),
  FlameSmall: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c0 4-3 5-3 8a3 3 0 1 0 6 0c0-1.5-1-2-1-4 2 1 4 3 4 7a6 6 0 1 1-12 0c0-4 3-5 3-8z"/></svg>
  ),
  Star: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 2 L14 9 L22 10 L16 15 L18 22 L12 18 L6 22 L8 15 L2 10 L10 9 Z" fill="currentColor"/></svg>
  ),
  Chevron: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
  ),
  User: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/></svg>
  ),
  Gear: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
  ),
  Logout: (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/></svg>
  ),
  Lines: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 5h16M4 12h16M4 19h10"/></svg>
  ),
  ChipStar: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 2 L14 9 L22 10 L16 15 L18 22 L12 18 L6 22 L8 15 L2 10 L10 9 Z"/></svg>
  ),
  Arrow: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14m-6-6 6 6-6 6"/></svg>
  ),
  Doc: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13h6m-6 4h4"/></svg>
  ),
  Trend: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18l5-6 4 4 8-10"/></svg>
  ),
  Bolt: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2 3 14h7l-1 8 10-12h-7z"/></svg>
  ),
  Calendar: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>
  ),
  Trophy: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V6a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v3"/><path d="M4 9h16v3a8 8 0 1 1-16 0z"/><path d="M12 17v4M8 21h8"/></svg>
  ),
  Clock: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
  ),
  Chat: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-13 8L3 21l1-5A9 9 0 1 1 21 12z"/></svg>
  ),
  Reading: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M2 5l10 2 10-2v14l-10 2L2 19z"/></svg>
  ),
  ReadingSm: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M2 5l10 2 10-2v14l-10 2L2 19z"/></svg>
  ),
  Maths: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 21l5-5m9-13l3 3m0 0L6 21H3v-3L18 3z"/></svg>
  ),
  MathsSm: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 21l5-5m9-13l3 3m0 0L6 21H3v-3L18 3z"/></svg>
  ),
  ThinkingDial: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>
  ),
  ThinkingCheck: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="9"/><path d="M9 12l2 2 4-4"/></svg>
  ),
  ThinkingSm: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l3 2"/></svg>
  ),
  Writing: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 12h4l3-8 4 16 3-8h4"/></svg>
  ),
  WritingSm: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 12h4l3-8 4 16 3-8h4"/></svg>
  ),
  MockBars: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 4v16M12 4v16M19 4v16M5 8h14M5 16h14"/></svg>
  ),
  ClipCheck: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></svg>
  ),
  EmptyTrend: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18l5-6 4 4 8-10"/></svg>
  ),
  EmptyChat: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-13 8L3 21l1-5A9 9 0 1 1 21 12z"/></svg>
  ),
  EmptyTrophy: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V6a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v3"/><path d="M4 9h16v3a8 8 0 1 1-16 0z"/><path d="M12 17v4M8 21h8"/></svg>
  ),
  TabHome: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2z"/></svg>
  ),
  TabPractice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9h12l-1 11H7z"/><path d="M9 9V7a3 3 0 1 1 6 0v2"/></svg>
  ),
  TabQuest: (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 2 3 14h7l-1 8 10-12h-7z"/></svg>
  ),
  TabSchedule: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 9h18M8 3v4M16 3v4"/></svg>
  ),
  TabMe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>
  ),
};

// ────────────────────────────────────────────────────────────────────────────
// Mock data — verbatim from portal-dashboard.html
// ────────────────────────────────────────────────────────────────────────────
const MOCK_ACTIVE = {
  firstName: 'Ace',
  lastInitial: 'Y.',
  streakDays: 7,
  pointsTotal: 2140,
  yearLabel: 'Year 4 · Opportunity Class',
  levelLabel: 'Level 4',
  topChip: 'Top 12% nationally',
  greetingSub: "You're three days into the OC home stretch. Knock out today's 15-minute quest and keep that streak hopping.",
  weeklyHabit: [
    { day: 'M', status: 'done', date: '' },
    { day: 'T', status: 'done', date: '' },
    { day: 'W', status: 'done', date: '' },
    { day: 'T', status: 'done', date: '' },
    { day: 'F', status: 'today', date: '' },
    { day: 'S', status: 'pending', date: '' },
    { day: 'S', status: 'pending', date: '' },
  ] as Array<{ day: string; status: string; date: string }>,
  questTitle: 'Crack three OC reasoning puzzles, hop forward.',
  questSub: "Three short steps. Each is <5 minutes. Finish all three to claim today's 50 points and lock in your streak.",
  questSteps: [
    { id: 's1', title: 'Daily warm-up — 5 quick analogies', meta: 'Completed 8:42am · Scored 5/5', time: '✓ Done', done: true },
    { id: 's2', title: 'Pattern reasoning — Block 7', meta: '10 questions · OC-style · adaptive difficulty', time: '~7 min', done: false },
    { id: 's3', title: 'Mistake debrief — yesterday’s 2 misses', meta: 'Friendly walk-throughs · no scoring', time: '~3 min', done: false },
  ] as QuestStepVM[],
  pending: [
    { id: 'p1', letter: 'M', tone: 'coral', title: 'Mathematical reasoning — Set 12', meta: '20 questions · timed · 30 min', due: 'Today', dueVariant: 'today' },
    { id: 'p2', letter: 'T', tone: 'grape', title: 'Thinking skills — Block 4', meta: '15 questions · timed · 25 min', due: 'Tomorrow', dueVariant: 'soon' },
    { id: 'p3', letter: 'R', tone: 'green', title: 'Reading comprehension — Story 8', meta: '12 questions · untimed', due: 'Fri', dueVariant: 'later' },
  ] as PendingVM[],
  progress: [
    { id: 'reading', name: 'Reading comprehension', delta: '+6%', pct: 78, tone: 'green' as SubjectTone },
    { id: 'maths', name: 'Mathematical reasoning', delta: '+4%', pct: 82, tone: 'orange' as SubjectTone },
    { id: 'thinking', name: 'Thinking skills', delta: '+3%', pct: 76, tone: 'grape' as SubjectTone },
    { id: 'writing', name: 'Writing & expression', delta: 'Started last Tuesday', pct: 34, tone: 'coral' as SubjectTone },
  ] as SubjectProgressVM[],
  qa: [
    { id: 'reading', name: 'Reading', count: '12', unit: 'tests ready', tone: 'green' as SubjectTone },
    { id: 'maths', name: 'Maths', count: '13', unit: 'sets', tone: 'orange' as SubjectTone },
    { id: 'thinking', name: 'Thinking skills', count: '9', unit: 'blocks', tone: 'grape' as SubjectTone },
    { id: 'mock', name: 'Mock exam', count: '2', unit: 'waiting', tone: 'coral' as SubjectTone },
  ] as QATile[],
  upcoming: [
    { id: 'u1', day: 17, weekday: 'Fri', title: 'OC Maths · Live class', meta: '4:30pm · 45 min · Ms. Chen', initials: 'MC', tone: 1 as const },
    { id: 'u2', day: 19, weekday: 'Sun', title: 'Thinking skills workshop', meta: '10am · 60 min · Mr. Patel', initials: 'RP', tone: 2 as const },
    { id: 'u3', day: 20, weekday: 'Mon', title: 'Mock exam · OC trial 3', meta: 'Anytime · 90 min · Self-paced', initials: '★', tone: 3 as const },
  ] as UpcomingVM[],
  achievements: [
    { id: 'a1', name: 'Consistent Learner', prog: '7-day streak', unlocked: true, tone: 'coral' as const, glyph: '★' },
    { id: 'a2', name: 'Reading Pro', prog: '10 / 10 tests', unlocked: true, tone: 'green' as const, glyph: '📖' },
    { id: 'a3', name: 'Math Whiz', prog: '88% · need 90', unlocked: false, glyph: '★' },
    { id: 'a4', name: 'Thinking Expert', prog: 'Reach Adv. level', unlocked: false, glyph: '⚡' },
  ] as AchievementVM[],
  activity: [
    { id: 'ac1', text: (<><b>Daily warm-up</b> · 5 analogies</>), sub: 'Reasoning · adaptive', score: '5 / 5', scoreVariant: 'green' as const, tone: 'green' as const },
    { id: 'ac2', text: (<><b>Pattern reasoning</b> · Block 6</>), sub: 'Yesterday · 6:14pm', score: '8 / 10', scoreVariant: 'orange' as const, tone: 'coral' as const },
    { id: 'ac3', text: (<><b>Mistake debrief</b> · 2 walk-throughs</>), sub: 'Yesterday · 6:32pm', timeOnly: '5 min', tone: 'grape' as const },
    { id: 'ac4', text: (<><b>Reading · Story 7</b> · comprehension</>), sub: 'Wednesday · 4:48pm', score: '11 / 12', scoreVariant: 'green' as const, tone: 'orange' as const },
  ] as ActivityVM[],
  feedback: [
    { id: 'f1', initials: 'MC', name: 'Ms. Chen · Maths', when: 'Yesterday', body: (<>"Great instincts on the <b>pattern questions</b> this week, Ace. Try slowing down on the multi-step ones — your accuracy will jump."</>) },
    { id: 'f2', initials: 'RP', name: 'Mr. Patel · Thinking', when: 'Tuesday', body: (<>"Loved your reasoning on Block 5. <b>Same approach</b> in Sunday's workshop and you'll smash it."</>) },
  ] as FeedbackVM[],
};

// ────────────────────────────────────────────────────────────────────────────
// Analytics helper — fire-and-forget
//   Skipped entirely when in preview mode so a deployed static demo
//   doesn't spam a missing/CORS-blocked backend.
// ────────────────────────────────────────────────────────────────────────────
const fireAnalytics = (
  eventType: string,
  payload: Record<string, any> = {},
  options: { previewMode?: boolean } = {}
) => {
  if (options.previewMode) return;
  void track(eventType, { properties: payload });
};

// ────────────────────────────────────────────────────────────────────────────
// Small presentational helpers
// ────────────────────────────────────────────────────────────────────────────
const subjectIcon = (tone: SubjectTone): React.ReactNode => {
  if (tone === 'green') return Icon.Reading;
  if (tone === 'orange') return Icon.Maths;
  if (tone === 'grape') return Icon.ThinkingDial;
  return Icon.Writing;
};
const subjectIconSm = (tone: SubjectTone): React.ReactNode => {
  if (tone === 'green') return Icon.ReadingSm;
  if (tone === 'orange') return Icon.MathsSm;
  if (tone === 'grape') return Icon.ThinkingSm;
  return Icon.WritingSm;
};
const qaIcon = (tone: SubjectTone): React.ReactNode => {
  if (tone === 'green') return Icon.Reading;
  if (tone === 'orange') return Icon.Maths;
  if (tone === 'grape') return Icon.ThinkingCheck;
  return Icon.MockBars;
};
const cssVar = (tone: SubjectTone): string => {
  if (tone === 'green') return 'var(--green)';
  if (tone === 'orange') return 'var(--orange)';
  if (tone === 'grape') return 'var(--grape)';
  return 'var(--coral)';
};

// ────────────────────────────────────────────────────────────────────────────
// Recommended practice (band-filtered, driven by the learning profile)
// ────────────────────────────────────────────────────────────────────────────
const RecommendedPractice: React.FC<{
  data: RecommendedTestsResponse;
  onStart: (t: { id: string; title: string }) => void;
}> = ({ data, onStart }) => {
  const comingSoonLabel =
    data.comingSoon && data.targetExam
      ? EXAM_META[data.targetExam]?.label
      : null;

  return (
    <section style={{ marginBottom: 20 }}>
      <div className="cvr-card">
        <div className="cvr-card__head">
          <div
            className="cvr-ico"
            style={{ background: 'var(--orange-soft)', color: 'var(--orange)' }}
          >
            🎯
          </div>
          <h2>Recommended for you · Year {data.band} NAPLAN</h2>
        </div>

        {comingSoonLabel && (
          <div
            style={{
              margin: '0 0 12px',
              padding: '10px 12px',
              borderRadius: 10,
              background: '#fff4e0',
              color: '#9a5a12',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {comingSoonLabel} content is coming soon — in the meantime, here&rsquo;s
            Year {data.band} NAPLAN practice to keep building the same core skills.
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
          }}
        >
          {data.tests.slice(0, 6).map((t) => (
            <button
              key={t.id}
              type="button"
              className="cvr-reco-card"
              onClick={() => onStart({ id: t.id, title: t.title })}
              style={{
                textAlign: 'left',
                border: '1.5px solid #e5e7eb',
                borderRadius: 12,
                padding: '12px 14px',
                background: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink)' }}>
                  {t.title}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {t.estimatedTime ? `~${t.estimatedTime} min · ` : ''}
                  {t.questionCount
                    ? `${t.questionCount} question${Number(t.questionCount) === 1 ? '' : 's'}`
                    : t.totalMarks
                    ? `${t.totalMarks} mark${Number(t.totalMarks) === 1 ? '' : 's'}`
                    : 'Practice test'}
                </div>
              </div>
              {/* Explicit affordance — the plain card didn't read as tappable (ROO-220). */}
              <span aria-hidden="true" style={{ color: 'var(--anchor)', fontWeight: 800, fontSize: 16 }}>→</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────
type StudentDashboardProps = {
  /** Force a preview state regardless of URL params. Used by /demo routes. */
  forcedPreview?: 'active' | 'empty';
};

const StudentDashboard: React.FC<StudentDashboardProps> = ({ forcedPreview }) => {
  // The nested sub-view (placement/test/result/assessment) is derived from the
  // URL below (see currentView). Transient data for the in-app test runner:
  const [currentTestId, setCurrentTestId] = useState<string | null>(null);
  const [currentTestTitle, setCurrentTestTitle] = useState<string>('');
  const [testResult, setTestResult] = useState<any>(null);

  // Preview switch
  const [searchParams, setSearchParams] = useSearchParams();
  const preview = forcedPreview ?? searchParams.get('preview'); // 'active' | 'empty' | null

  // Auth
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();

  // ── URL is the source of truth for which screen shows (ROO-119) ────────────
  // Every view is a real route, so navigation updates the address bar, is
  // deep-linkable, and Back/Forward works. setView/setCurrentView are kept as
  // thin navigate() wrappers so existing call sites need no change.
  const location = useLocation();
  const path = location.pathname;
  const practiceSkill = path.startsWith('/practice/')
    ? decodeURIComponent(path.slice('/practice/'.length))
    : '';
  const currentView: 'dashboard' | 'assessment' | 'test' | 'result' | 'placement' | 'report' | 'practice' | 'recommended' | 'wrong-questions' | 'mock-exams' | 'checkin' =
    path === '/placement' ? 'placement'
    : path === '/assessment' ? 'assessment'
    : path === '/test' ? 'test'
    : path === '/result' ? 'result'
    : path === '/report' ? 'report'
    : path === '/practice/recommended' ? 'recommended'
    : path === '/practice/checkin' ? 'checkin'
    : path.startsWith('/practice/') ? 'practice'
    : path === '/wrong-questions' ? 'wrong-questions'
    : path === '/mock-exams' ? 'mock-exams'
    : 'dashboard';
  const setCurrentView = (
    v: 'dashboard' | 'assessment' | 'test' | 'result' | 'placement',
  ) => navigate(v === 'dashboard' ? '/' : `/${v}`);
  // /resources and the in-app article reader /resources/:slug both live under
  // the "resources" view (ROO-202).
  const articleSlug = path.startsWith('/resources/') ? path.slice('/resources/'.length) : null;
  const view: 'dashboard' | 'progress' | 'resources' | 'achievements' =
    path === '/progress' ? 'progress'
    : path === '/achievements' ? 'achievements'
    : path === '/resources' || articleSlug ? 'resources'
    : 'dashboard';
  const setView = (v: 'dashboard' | 'progress' | 'resources' | 'achievements') =>
    navigate(v === 'dashboard' ? '/' : `/${v}`);
  const currentUser = useMemo(() => {
    if (authUser) return authUser;
    try {
      const saved = localStorage.getItem('cleveroo_user');
      if (saved) return JSON.parse(saved);
    } catch { /* noop */ }
    return null;
  }, [authUser]);

  // Account dropdown (Profile / Settings / Log out) in the top-right nav.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Close when the click lands outside every account-menu container
      // (there's one in the desktop nav and one in the mobile top bar).
      if (!target.closest || !target.closest('.cvr-usermenu')) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  // `view` (dashboard/progress/resources) and `setView` are derived from the URL
  // above (ROO-119) so the active section is reflected in the address bar.

  // Real-data states (only used when preview is null)
  const [loading, setLoading] = useState(true);
  const [enrolledClasses, setEnrolledClasses] = useState<EnrolledClass[]>([]);
  const [pendingTests, setPendingTests] = useState<PendingTest[]>([]);
  const [categoryStats, setCategoryStats] = useState<DashboardData | null>(null);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [recommended, setRecommended] = useState<RecommendedTestsResponse | null>(null);
  const [achievementsData, setAchievementsData] = useState<AchievementVM[] | null>(null);
  const [upcomingData, setUpcomingData] = useState<UpcomingVM[] | null>(null);
  const [resourcesData, setResourcesData] = useState<ResourceVM[] | null>(null);
  const [questData, setQuestData] = useState<{ steps: QuestStepVM[]; raw: any[] } | null>(null);

  // Ref for scrolling to quest section on mobile tab tap
  const questSectionRef = useRef<HTMLDivElement>(null);

  // Onboarding progress flags (fetched from /dashboard endpoint)
  const [placementCompleted, setPlacementCompleted] = useState<boolean>(false);
  const [weeklyGoalMinutes, setWeeklyGoalMinutes] = useState<number | null>(null);

  // Cached placement report for the /report re-view screen (ROO-132).
  const [savedReport, setSavedReport] = useState<PlacementResult | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  useEffect(() => {
    if (currentView !== 'report' || !currentUser?.id) return;
    let cancelled = false;
    setReportLoading(true);
    // No report → stay on /report and show the explanatory empty state below
    // (used to silently bounce to the dashboard — ROO-217).
    getPlacementReport(currentUser.id)
      .then((r) => { if (!cancelled) setSavedReport(r); })
      .catch(() => { if (!cancelled) setSavedReport(null); })
      .finally(() => { if (!cancelled) setReportLoading(false); });
    return () => { cancelled = true; };
  }, [currentView, currentUser?.id, navigate]);

  // Also load the cached report on the dashboard to build the "focus areas" plan (ROO-134).
  useEffect(() => {
    if (savedReport || !placementCompleted || !currentUser?.id || preview) return;
    let cancelled = false;
    getPlacementReport(currentUser.id).then((r) => { if (!cancelled && r) setSavedReport(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [placementCompleted, currentUser?.id, savedReport, preview]);

  // Practice volume since the last assessment → retake nudge (ROO-161).
  const [practiceProgress, setPracticeProgress] = useState<PracticeProgress | null>(null);
  useEffect(() => {
    if (!placementCompleted || !currentUser?.id || preview || currentView !== 'dashboard') return;
    let cancelled = false;
    getPracticeProgress(currentUser.id).then((p) => { if (!cancelled && p) setPracticeProgress(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [placementCompleted, currentUser?.id, preview, currentView]);

  // Effort level + one-shot level-up celebration (ROO-172 A).
  const [engagement, setEngagement] = useState<EngagementData | null>(null);
  useEffect(() => {
    if (!currentUser?.id || preview || currentView !== 'dashboard') return;
    let cancelled = false;
    getEngagement(currentUser.id).then((e) => { if (!cancelled && e) setEngagement(e); }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.id, preview, currentView]);

  // Opt-in class leaderboard (ROO-172 C).
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null);
  useEffect(() => {
    if (!currentUser?.id || preview || currentView !== 'dashboard') return;
    let cancelled = false;
    getLeaderboard(currentUser.id).then((l) => { if (!cancelled && l) setLeaderboard(l); }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser?.id, preview, currentView]);

  // Goal picker modal visibility
  const [showGoalPicker, setShowGoalPicker] = useState(false);

  // Branded confirm for the streak-makeup flow (ROO-221).
  const { confirm: makeupConfirm, dialog: makeupDialog } = useConfirm();

  // True when a core dashboard fetch failed (ROO-215): we render honest empty
  // sections rather than fake data, but the student must SEE that something
  // didn't load instead of mistaking it for "no progress".
  const [fetchFailed, setFetchFailed] = useState(false);

  // Server week strip with missed/made-up days + real dates (ROO-221).
  const [serverHabit, setServerHabit] = useState<Array<{ day: string; status: string; date: string }> | null>(null);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  // ────────────────────────────────────────────────────────────────────────
  // Real data fetching (preserved from previous implementation)
  // ────────────────────────────────────────────────────────────────────────
  const fetchDashboardData = useCallback(async (studentId: string) => {
    try {
      const token =
        localStorage.getItem('cleveroo_token') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/dashboard`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: false,
      });
      if (res.data?.success) {
        const d = res.data.data;
        setEnrolledClasses(d.enrolledClasses || []);
        setPendingTests(d.pendingTests || []);
        // Server-derived week strip incl. missed/made-up days (ROO-221).
        if (Array.isArray(d.weeklyHabit)) setServerHabit(d.weeklyHabit);
        // Onboarding flags — may be at top level or nested under onboarding object
        const placed =
          d.placementCompleted ??
          d.onboarding?.placementCompleted ??
          false;
        setPlacementCompleted(Boolean(placed));
        const goalMins =
          d.weekly_goal_minutes ??
          d.weeklyGoalMinutes ??
          d.onboarding?.weekly_goal_minutes ??
          null;
        setWeeklyGoalMinutes(goalMins != null ? Number(goalMins) : null);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setFetchFailed(true);
    }
  }, [API_BASE_URL]);

  // Real per-user summary. NOTE: must NOT fall back to mock data — a mocked
  // testsCompleted (26) would make isDayOne false and hide the day-one view /
  // placement CTA for genuinely-new students (ROO-98). Leaves categoryStats null
  // on error instead.
  const fetchCategoryStats = useCallback(async (studentId: string) => {
    try {
      const token = localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
      const res = await axios.get(`${API_BASE_URL}/api/user-stats/summary/${studentId}`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: false,
      });
      if (res.data?.success) setCategoryStats(res.data.data as DashboardData);
    } catch (err) {
      console.error('Failed to fetch category stats:', err);
      setFetchFailed(true);
    }
  }, [API_BASE_URL]);

  const fetchRecentActivity = useCallback(async (studentId: string) => {
    try {
      const token = localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
      const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/activity?limit=5`, {
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        withCredentials: false,
      });
      if (res.data?.success) setActivityData(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch recent activity:', err);
      setFetchFailed(true);
    }
  }, [API_BASE_URL]);

  // Band-filtered recommended NAPLAN practice (driven by learning profile).
  const fetchRecommended = useCallback(async (studentId: string) => {
    try {
      const data = await getRecommendedTests(studentId);
      setRecommended(data);
    } catch (err) {
      console.error('Failed to fetch recommended tests:', err);
      setRecommended(null);
    }
  }, []);

  const fetchAchievements = useCallback(async (studentId: string) => {
    try {
      const token =
        localStorage.getItem('cleveroo_token') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/achievements`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: false,
      });
      if (res.data?.success) setAchievementsData(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch achievements:', err);
    }
  }, [API_BASE_URL]);

  const fetchUpcomingClasses = useCallback(async (studentId: string) => {
    try {
      const token =
        localStorage.getItem('cleveroo_token') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/upcoming-classes`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: false,
      });
      if (res.data?.success) setUpcomingData(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch upcoming classes:', err);
    }
  }, [API_BASE_URL]);

  const fetchResources = useCallback(async (studentId: string) => {
    try {
      const token =
        localStorage.getItem('cleveroo_token') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/resources`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: false,
      });
      if (res.data?.success) setResourcesData(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    }
  }, [API_BASE_URL]);

  const fetchQuestToday = useCallback(async (studentId: string) => {
    try {
      const token =
        localStorage.getItem('cleveroo_token') ||
        localStorage.getItem('accessToken') ||
        '';
      const res = await axios.get(`${API_BASE_URL}/api/students/${studentId}/quest/today`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        withCredentials: false,
      });
      const quest = res.data;
      if (quest?.steps) {
        const steps: QuestStepVM[] = quest.steps.map((s: any) => ({
          id: s.id,
          title: s.title,
          meta: s.meta,
          time: s.status === 'done'
            ? `✓ Done${s.score ? ` · ${s.score}` : ''}`
            : `~${s.minutes ?? 5} min`,
          done: s.status === 'done',
          deepLink: s.deepLink,
        }));
        setQuestData({ steps, raw: quest.steps });
      }
    } catch (err) {
      console.error('Failed to fetch quest/today:', err);
    }
  }, [API_BASE_URL]);

  // Load real data only when no preview override
  useEffect(() => {
    if (preview) {
      setLoading(false);
      return;
    }
    const studentId = currentUser?.id;
    if (!studentId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchDashboardData(studentId),
      fetchCategoryStats(studentId),
      fetchRecentActivity(studentId),
      fetchRecommended(studentId),
      fetchAchievements(studentId),
      fetchUpcomingClasses(studentId),
      fetchResources(studentId),
      fetchQuestToday(studentId),
    ]).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [preview, currentUser?.id, fetchDashboardData, fetchCategoryStats, fetchRecentActivity, fetchRecommended, fetchAchievements, fetchUpcomingClasses, fetchResources, fetchQuestToday]);

  // ────────────────────────────────────────────────────────────────────────
  // Derive view-model from preview / real data
  // ────────────────────────────────────────────────────────────────────────
  const userStats = categoryStats?.userStats;

  // For real-data path, build a VM that approximates the design's content
  const realVM = useMemo(() => {
    const firstName = (currentUser?.name || 'Student').split(' ')[0] || 'Student';
    const lastInitial = (currentUser?.name || '').split(' ').slice(1).map((p: string) => p[0]).join('.') || '';
    const streakDays = userStats?.studyStreak ?? 0;
    const pointsTotal = userStats?.testsCompleted ? userStats.testsCompleted * 50 : 0;

    // Weekly habit — prefer the server strip (real per-day data incl. missed/
    // made-up days + dates, ROO-221); fall back to the streak-derived guess.
    const weeklyHabit = serverHabit
      ? serverHabit.map((d) => ({ day: (d.day || '?')[0], status: d.status, date: d.date }))
      : Array.from({ length: 7 }).map((_, idx) => {
          const dayLetters = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
          const today = new Date().getDay(); // 0=Sun..6=Sat -> remap to Mon=0
          const todayIdx = (today + 6) % 7;
          let status: string = 'pending';
          if (idx < todayIdx && idx >= todayIdx - streakDays) status = 'done';
          if (idx === todayIdx) status = 'today';
          return { day: dayLetters[idx], status, date: '' };
        });

    const toneOfCategory = (cat: string): SubjectTone => {
      const c = cat.toLowerCase();
      if (c.includes('reading')) return 'green';
      if (c.includes('math') || c.includes('numeracy')) return 'orange';
      if (c.includes('think')) return 'grape';
      return 'coral';
    };

    // Display names for user_test_records.test_category values (ROO-214).
    const categoryDisplayName = (cat: string): string => {
      const NAMES: Record<string, string> = {
        reading: 'Reading comprehension',
        mathematical: 'Mathematical reasoning',
        thinking_skill: 'Thinking skills',
        language_conventions: 'Language conventions',
        numeracy: 'Numeracy',
        writing: 'Writing',
      };
      return NAMES[cat] || cat.replace(/[_-]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
    };

    const toneOfArea = (area: string): SubjectTone => {
      const a = area.toLowerCase();
      if (a.includes('read')) return 'green';
      if (a.includes('num') || a.includes('math')) return 'orange';
      if (a.includes('think')) return 'grape';
      return 'coral'; // language_conventions / writing / other
    };

    const testStatsList = categoryStats?.testStats || [];
    const hasTestData = testStatsList.some(
      (s) => (s.progress?.completed || 0) > 0 || (s.performance?.averageScore || 0) > 0,
    );
    const placementBreakdown = categoryStats?.placement?.breakdown || null;

    // Prefer real test performance; otherwise seed from the placement result so a
    // student who has only done placement still sees Subject Progress (ROO-207).
    const progress: SubjectProgressVM[] = hasTestData
      ? testStatsList.map((s) => ({
          id: s.testType,
          name: categoryDisplayName(s.testType),
          delta: '',
          pct: Math.round(s.performance?.averageScore || 0),
          tone: toneOfCategory(s.testType),
        }))
      : (placementBreakdown || []).map((b) => ({
          id: `placement-${b.area}`,
          name: b.label,
          delta: 'From your placement',
          pct: Math.round(b.percent || 0),
          tone: toneOfArea(b.area),
        }));

    const qa: QATile[] = (categoryStats?.testStats || []).map((s) => {
      // Honest numbers only: progress.total is a synthetic "completed+20,
      // min 100" figure, so "100 sets" was fabricated (ROO-220). Show what we
      // actually know — how many the student has done.
      const completed = s.progress?.completed || 0;
      return {
        id: s.testType,
        name: s.testType === 'reading' ? 'Reading'
          : s.testType === 'mathematical' ? 'Maths'
          : categoryDisplayName(s.testType),
        count: completed > 0 ? String(completed) : 'Start',
        unit: completed > 0 ? 'done' : '',
        tone: toneOfCategory(s.testType),
      };
    });

    const pending: PendingVM[] = pendingTests.slice(0, 6).map((t) => {
      const tone = toneOfCategory(t.subject || '');
      const due = (() => {
        if (!t.due_date) return { label: 'No date', variant: 'later' as DueVariant };
        const d = new Date(t.due_date);
        const ms = d.getTime() - Date.now();
        const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
        if (days <= 0) return { label: 'Today', variant: 'today' as DueVariant };
        if (days === 1) return { label: 'Tomorrow', variant: 'soon' as DueVariant };
        return { label: d.toLocaleDateString(undefined, { weekday: 'short' }), variant: 'later' as DueVariant };
      })();
      return {
        id: t.test_id,
        letter: (t.subject || '?')[0].toUpperCase(),
        tone,
        title: t.title,
        meta: `${t.total_marks || '—'} marks · ${t.estimated_time || 0} min`,
        due: due.label,
        dueVariant: due.variant,
      };
    });

    const CATEGORY_LABELS: Record<string, string> = {
      mathematical: 'Maths practice',
      reading: 'Reading practice',
      thinking_skill: 'Thinking skills practice',
      language_conventions: 'Language conventions practice',
      numeracy: 'Numeracy practice',
      writing: 'Writing practice',
    };
    const activity: ActivityVM[] = (activityData || []).slice(0, 5).map((a, i) => {
      const rawScore = a.score ?? a.score_percentage;
      const score = rawScore != null ? `${Math.round(Number(rawScore))}%` : undefined;
      const label = CATEGORY_LABELS[a.category] || a.type || a.test_category || 'Activity';
      const when = a.date || a.completed_at;
      return {
        id: a.id || `a${i}`,
        text: (<><b>{label}</b> completed</>),
        sub: when
          ? new Date(when).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
          : '',
        score,
        scoreVariant: (rawScore != null && Number(rawScore) < 50 ? 'coral' : rawScore != null && Number(rawScore) < 80 ? 'orange' : 'green') as ActivityVM['scoreVariant'],
        // Dot colour mirrors the score (was cycling by list position, which
        // made a 100% row show a red dot — ROO-213 polish).
        tone: rawScore == null ? 'grape'
          : Number(rawScore) < 50 ? 'coral'
          : Number(rawScore) < 80 ? 'orange'
          : 'green',
      } as ActivityVM;
    });

    return {
      firstName,
      lastInitial,
      streakDays,
      pointsTotal,
      yearLabel: enrolledClasses[0]?.grade ? `${enrolledClasses[0].grade} · Opportunity Class` : 'Cleveroo',
      levelLabel: `Level ${Math.max(1, Math.floor((userStats?.testsCompleted || 0) / 5) + 1)}`,
      topChip: '',
      greetingSub: "Ready for today’s quest? Stay on the streak and keep hopping.",
      weeklyHabit,
      questTitle: 'Crack today’s reasoning puzzles, hop forward.',
      questSub: 'Three short steps. Finish all three to lock in your streak.',
      // Real data only — never fall back to MOCK_ACTIVE here, or a fetch failure
      // shows fabricated streaks/badges/classes as the student's own (ROO-191).
      // MOCK_ACTIVE is reserved for preview === 'active' (the demo VM below).
      questSteps: questData?.steps ?? [],
      pending,
      progress,
      qa,
      upcoming: upcomingData ?? [],
      achievements: achievementsData ?? [],
      activity,
      feedback: [] as FeedbackVM[], // Teacher-feedback section removed (ROO-181)
    };
  }, [currentUser, categoryStats, userStats, pendingTests, enrolledClasses, activityData, achievementsData, upcomingData, questData, serverHabit]);

  // Decide which VM and which view (active vs day-1)
  const isPreviewActive = preview === 'active';
  const isPreviewEmpty = preview === 'empty';
  const useMock = isPreviewActive;

  const isDayOne = isPreviewEmpty
    ? true
    : (!useMock && !loading
        && !placementCompleted // once placement is done, show the full dashboard (ROO-207)
        && pendingTests.length === 0
        && activityData.length === 0
        && (categoryStats?.userStats?.testsCompleted ?? 0) === 0);

  const vm = useMock ? MOCK_ACTIVE : realVM;

  // ────────────────────────────────────────────────────────────────────────
  // One-time mount analytics
  // ────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (loading && !preview) return; // wait until real data settled (unless preview)
    const previewMode = !!preview;
    fireAnalytics('dashboard_loaded', { state: isDayOne ? 'empty' : 'active' }, { previewMode });
    const days = vm.streakDays;
    if (days > 0 && days % 7 === 0) {
      fireAnalytics('streak_milestone_hit', { streakDays: days }, { previewMode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, preview, isDayOne]);

  // ────────────────────────────────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────────────────────────────────
  const handleStartTest = (testId: string, testTitle: string) => {
    setCurrentTestId(testId);
    setCurrentTestTitle(testTitle);
    // testId lives in the query string so the URL is a real deep link
    // (shareable / cross-device, ROO-220); router state keeps title fidelity.
    const q = new URLSearchParams({ testId, title: testTitle });
    navigate(`/test?${q.toString()}`, { state: { testId, testTitle } });
  };
  const handleTestComplete = (result: any) => {
    setTestResult(result);
    navigate('/result', { state: { result, testTitle: currentTestTitle } });
    if (currentUser?.id) fetchDashboardData(currentUser.id);
  };
  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setCurrentTestId(null);
    setCurrentTestTitle('');
    setTestResult(null);
  };
  const handleCancelTest = () => {
    setCurrentView('dashboard');
    setCurrentTestId(null);
    setCurrentTestTitle('');
    // Refresh stats so a completed Reading Challenge (which exits via onCancel)
    // is reflected on the dashboard immediately.
    if (currentUser?.id) fetchDashboardData(currentUser.id);
  };

  const previewMode = !!preview;

  // Quest steps — clicking a pending step starts it (navigate to deepLink or fallback analytics)
  const handleQuestStep = (stepId: string) => {
    fireAnalytics('quest_step_clicked', { stepId }, { previewMode });
    if (previewMode) return;
    const rawSteps = questData?.raw ?? [];
    const rawStep = rawSteps.find((s: any) => s.id === stepId);
    if (rawStep && rawStep.status === 'pending' && rawStep.deepLink) {
      navigate(rawStep.deepLink);
    }
  };

  // First incomplete quest step — used by "Continue quest" CTA
  const startFirstIncompleteQuestStep = () => {
    if (previewMode) return;
    const rawSteps = questData?.raw ?? [];
    const pending = rawSteps.find((s: any) => s.status === 'pending');
    if (pending?.deepLink) {
      navigate(pending.deepLink);
    }
  };

  // Subject tile — find a matching recommended test or navigate to the dashboard
  const handleSubjectTile = (subjectId: string) => {
    fireAnalytics('subject_tile_clicked', { subjectId }, { previewMode });
    if (previewMode) return;
    if (recommended?.tests && recommended.tests.length > 0) {
      const subjectLower = subjectId.toLowerCase();
      const match = recommended.tests.find((t) => {
        const titleLower = t.title.toLowerCase();
        if (subjectLower === 'reading') return titleLower.includes('read');
        if (subjectLower === 'maths' || subjectLower === 'mathematical') return titleLower.includes('math') || titleLower.includes('numer');
        if (subjectLower === 'thinking') return titleLower.includes('think');
        if (subjectLower === 'writing') return titleLower.includes('writ');
        return false;
      });
      if (match) {
        handleStartTest(match.id, match.title);
        return;
      }
    }
    // No matching test → targeted skill practice, never a dead click (ROO-174).
    const skillFor: Record<string, string> = {
      reading: 'reading', maths: 'numeracy', mathematical: 'numeracy',
      thinking: 'thinking_skill', writing: 'writing',
    };
    navigate(`/practice/${encodeURIComponent(skillFor[subjectId.toLowerCase()] || 'numeracy')}`);
  };

  const handlePlacementCta = () => {
    fireAnalytics('placement_cta_clicked', {}, { previewMode });
    if (!previewMode) setCurrentView('placement');
  };

  const handleGoalSaved = (minutes: number) => {
    setWeeklyGoalMinutes(minutes);
    setShowGoalPicker(false);
    // Re-fetch so the checklist reflects the update
    if (currentUser?.id) fetchDashboardData(currentUser.id);
  };

  // Streak makeup (ROO-221): tap a missed day → confirm → run the Quick
  // Check-in with the missed date attached; redemption happens on submit.
  const handleMakeupDay = async (date: string) => {
    if (previewMode || !date) return;
    const pretty = new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });
    const ok = await makeupConfirm({
      title: 'Make up this day?',
      body: `Finish a quick 5-question quiz and ${pretty} will count towards your streak.`,
      confirmLabel: "Let's go",
      cancelLabel: 'Not now',
    });
    if (ok) navigate(`/practice/checkin?makeup=${date}`);
  };

  // ────────────────────────────────────────────────────────────────────────
  // Sub-view passthroughs (preserve)
  // ────────────────────────────────────────────────────────────────────────
  const routeState = (location.state as any) || {};
  // testId also readable from the query string so /test?testId=… deep-links
  // (used by the mock-exams list, ROO-169).
  const queryTestId = searchParams.get('testId');
  const activeTestId = currentTestId ?? routeState.testId ?? queryTestId ?? null;
  const activeTestTitle = currentTestTitle || routeState.testTitle || searchParams.get('title') || '';
  const activeResult = testResult ?? routeState.result ?? null;
  if (currentView === 'test' && activeTestId) {
    return (
      <TestRunner
        testId={activeTestId}
        onComplete={handleTestComplete}
        onCancel={handleCancelTest}
      />
    );
  }
  if (currentView === 'result' && activeResult) {
    return (
      <TestResult
        result={activeResult}
        testTitle={activeTestTitle}
        onBackToDashboard={handleBackToDashboard}
      />
    );
  }
  if (currentView === 'assessment') {
    return <AssessmentDemo onBack={() => setCurrentView('dashboard')} />;
  }
  if (currentView === 'placement' && currentUser?.id) {
    const handlePlacementComplete = () => {
      setCurrentView('dashboard');
      setPlacementCompleted(true);
      fetchDashboardData(currentUser.id);
    };
    const handlePlacementCancel = () => setCurrentView('dashboard');
    return (
      <PlacementQuiz
        studentId={currentUser.id}
        onComplete={handlePlacementComplete}
        onCancel={handlePlacementCancel}
        onPractise={() => navigate('/')}
        onPractiseSkill={(area) => navigate(`/practice/${encodeURIComponent(area)}`)}
      />
    );
  }
  // Targeted by-skill practice (ROO-133).
  if (currentView === 'practice' && currentUser?.id && practiceSkill) {
    return (
      <PlacementQuiz
        key={practiceSkill}
        studentId={currentUser.id}
        mode="practice"
        skill={practiceSkill}
        onComplete={() => navigate('/')}
        onCancel={() => navigate('/')}
        onPractise={() => navigate('/')}
        onPractiseSkill={(area) => navigate(`/practice/${encodeURIComponent(area)}`)}
      />
    );
  }
  // 5-question mini check-in (ROO-171).
  if (currentView === 'checkin' && currentUser?.id) {
    return (
      <PlacementQuiz
        key="checkin"
        studentId={currentUser.id}
        mode="checkin"
        // When launched from a missed streak day (ROO-221), the quiz redeems
        // the makeup on submit.
        makeupDate={searchParams.get('makeup') || undefined}
        onComplete={() => navigate('/')}
        onCancel={() => navigate('/')}
        onPractise={() => navigate('/')}
        onPractiseSkill={(area) => navigate(`/practice/${encodeURIComponent(area)}`)}
      />
    );
  }
  // Recommended practice from the student's wrong answers (ROO-160).
  if (currentView === 'recommended' && currentUser?.id) {
    return (
      <PlacementQuiz
        key="recommended"
        studentId={currentUser.id}
        mode="recommended"
        onComplete={() => navigate('/')}
        onCancel={() => navigate('/')}
        onPractise={() => navigate('/')}
        onPractiseSkill={(area) => navigate(`/practice/${encodeURIComponent(area)}`)}
      />
    );
  }
  // Wrong-question book (错题本, ROO-160).
  if (currentView === 'wrong-questions' && currentUser?.id) {
    return (
      <WrongQuestionBook
        studentId={currentUser.id}
        onBack={() => navigate('/')}
        onPractiseRecommended={() => navigate('/practice/recommended')}
        onPractiseSkill={(skill) => navigate(`/practice/${encodeURIComponent(skill)}`)}
      />
    );
  }
  // Full-length timed mock exams (ROO-169).
  if (currentView === 'mock-exams' && currentUser?.id) {
    return (
      <MockExamList
        studentId={currentUser.id}
        onBack={() => navigate('/')}
        onStart={(examId, title) =>
          navigate(`/test?testId=${encodeURIComponent(examId)}&title=${encodeURIComponent(title)}`)}
      />
    );
  }
  // Re-view the cached placement report (ROO-132).
  if (currentView === 'report') {
    if (savedReport) {
      return (
        <ScoreReport
          result={savedReport}
          studentName={currentUser?.name ? currentUser.name.split(' ')[0] : null}
          onClose={() => navigate('/')}
          onPractise={() => navigate('/')}
          onPractiseSkill={(area) => navigate(`/practice/${encodeURIComponent(area)}`)}
        />
      );
    }
    return (
      <div className="cvr-portal" style={{ padding: 48, textAlign: 'center', color: '#6B7A6F' }}>
        {reportLoading ? (
          'Loading your report…'
        ) : (
          <div style={{ maxWidth: 460, margin: '48px auto', background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 20, padding: '36px 32px' }}>
            <div style={{ fontSize: 40 }} aria-hidden="true">📊</div>
            <h2 style={{ color: '#1A2A1F', fontSize: 20, margin: '12px 0 6px' }}>No report yet</h2>
            <p style={{ fontSize: 14.5, margin: '0 0 20px' }}>
              Finish your placement quiz and your personalised skill report will live here.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/placement')}
                style={{ background: '#14593C', color: '#fff', border: 0, borderRadius: 999, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                Start placement
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{ background: '#fff', color: '#14593C', border: '1.5px solid #E5D9BD', borderRadius: 999, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
              >
                Back to dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // Compute small bits shared by both views
  // ────────────────────────────────────────────────────────────────────────
  const firstName = vm.firstName || 'Friend';
  const firstInitial = firstName[0]?.toUpperCase() || 'A';
  // Chosen animal avatar (ROO-222) → image; falls back to the name initial.
  const avatarUrl = avatarImage((currentUser as any)?.avatar);
  const avatarNode: React.ReactNode = avatarUrl
    ? <img className="cvr-avatar-img" src={avatarUrl} alt="" />
    : firstInitial;

  // Focus/challenge/leaderboard/recommended stack — rendered right AFTER the
  // greeting hero, not above it (ROO-177: the hero was getting buried).
  const postHeroCards = (
    <>
            {/* One-shot level-up celebration (ROO-172 A) */}
            {!previewMode && engagement?.celebrate && engagement.level && (
              <section style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(90deg, #FDF6E3, #F5FBF0)', border: '1.5px solid #E8DFC5', borderRadius: 16, padding: '14px 20px', marginBottom: 18 }}>
                <span style={{ fontSize: 30 }} aria-hidden="true">{engagement.level.icon}</span>
                <div>
                  <div style={{ fontSize: 15.5, fontWeight: 800, color: '#1A2A1F' }}>
                    Level up! You&rsquo;ve reached {engagement.level.name}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7A6F' }}>
                    {engagement.level.answered.toLocaleString()} questions answered
                    {engagement.level.next && <> — {(engagement.level.next.threshold - engagement.level.answered).toLocaleString()} more to {engagement.level.next.icon} {engagement.level.next.name}</>}
                  </div>
                </div>
              </section>
            )}

            {/* Post-placement "your plan": weak skills front-and-centre (ROO-134) */}
            {/* TODO (ROO-156): surface savedReport?.recommendations here — top subject + first topic —
                 once the visual design for mixing subject-level and topic-level chips is confirmed.
                 Currently the breakdown chips below already cover the same data at the skill level. */}
            {!previewMode && placementCompleted && (() => {
              const planSkills = (savedReport?.breakdown || []).filter((b) => b.percent < 80).slice(0, 4);
              return (
                <section style={{ background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 16, padding: '18px 20px', marginBottom: 18 }}>
                  {/* header row: title + actions */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1A2A1F' }}>
                      {planSkills.length > 0 ? 'Your focus areas' : 'Your placement is done'}
                    </h2>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => navigate('/report')} style={{ border: 0, background: 'none', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                        View full report →
                      </button>
                      {/* Retake CTA (ROO-157) — always visible when placement is completed.
                          Turns solid once enough practice has accrued (ROO-161). */}
                      <button
                        type="button"
                        onClick={() => navigate('/placement')}
                        style={practiceProgress?.nudgeRetake
                          ? { border: 0, background: '#14593C', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '5px 14px', borderRadius: 999 }
                          : { border: '1.5px solid #5DAA3A', background: 'none', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '4px 12px', borderRadius: 999 }}
                      >
                        Take new assessment →
                      </button>
                    </div>
                  </div>
                  {/* Retake nudge: enough practice since the last assessment (ROO-161) */}
                  {practiceProgress?.nudgeRetake && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F5FBF0', border: '1.5px solid #C5E0B0', borderRadius: 12, padding: '8px 14px', margin: '8px 0 10px', fontSize: 13, color: '#14593C', fontWeight: 600 }}>
                      <span aria-hidden="true">🚀</span>
                      You&rsquo;ve practised {practiceProgress.practisedSinceAssessment} questions since your last assessment — take a new one and see how much you&rsquo;ve improved!
                    </div>
                  )}
                  {/* Stale-profile nudge: quick check-in after 5+ quiet days (ROO-171) */}
                  {!practiceProgress?.nudgeRetake && practiceProgress?.nudgeCheckin && (
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, background: '#FBF7EA', border: '1.5px solid #E8DFC5', borderRadius: 12, padding: '8px 14px', margin: '8px 0 10px', fontSize: 13, color: '#5B5030', fontWeight: 600 }}>
                      <span aria-hidden="true">👋</span>
                      It&rsquo;s been {practiceProgress.daysSinceActivity} days — do a quick 5-question check-in to keep your plan fresh.
                      <button
                        type="button"
                        onClick={() => navigate('/practice/checkin')}
                        style={{ border: 0, background: '#9A6215', color: '#fff', borderRadius: 999, padding: '4px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Quick check-in →
                      </button>
                    </div>
                  )}
                  {planSkills.length > 0 ? (
                    <>
                      <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#6B7A6F' }}>
                        Based on your placement — tap a skill to practise it.
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {planSkills.map((s) => (
                          <button
                            key={s.area}
                            type="button"
                            onClick={() => navigate(`/practice/${encodeURIComponent(s.area)}`)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 999, border: '1.5px solid #5DAA3A', background: '#DCEFCC', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                          >
                            {s.label || s.area}
                            <span style={{ fontWeight: 500, opacity: 0.75 }}>{s.percent}%</span>
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={{ margin: '0', fontSize: 13.5, color: '#6B7A6F' }}>
                      You&rsquo;re scoring well across all areas. See how much you&rsquo;ve improved — take a new assessment!
                    </p>
                  )}
                  {/* Wrong-answer-driven practice entry points (ROO-160) */}
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 14, paddingTop: 12, borderTop: '1px solid #F3EEE0' }}>
                    <button
                      type="button"
                      onClick={() => navigate('/practice/recommended')}
                      style={{ border: 0, background: '#14593C', color: '#fff', borderRadius: 999, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      Practise your tricky questions →
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/wrong-questions')}
                      style={{ border: 0, background: 'none', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                    >
                      See tricky questions
                    </button>
                    {/* Progress-over-time entry point (ROO-166) */}
                    <button
                      type="button"
                      onClick={() => navigate('/progress')}
                      style={{ border: 0, background: 'none', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                    >
                      My progress
                    </button>
                    {/* Mock exams entry point (ROO-169) */}
                    <button
                      type="button"
                      onClick={() => navigate('/mock-exams')}
                      style={{ border: 0, background: 'none', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                    >
                      Mock exams
                    </button>
                  </div>
                </section>
              );
            })()}
            {/* Weekly challenge tied to the weakest skill (ROO-172 B) */}
            {!previewMode && placementCompleted && engagement?.weeklyChallenge && (() => {
              const wc = engagement.weeklyChallenge;
              const bar = (done: number, target: number, color: string) => (
                <div style={{ background: '#F3EEE0', borderRadius: 999, height: 10, overflow: 'hidden', flex: 1 }}>
                  <div style={{ width: `${target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0}%`, height: '100%', background: color, borderRadius: 999, transition: 'width .3s ease' }} />
                </div>
              );
              return (
                <section style={{ background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 16, padding: '18px 20px', marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1A2A1F' }}>
                      {wc.completed ? '🏆 Weekly challenge complete!' : 'This week’s challenge'}
                    </h2>
                    {!wc.completed && (
                      <button type="button" onClick={() => navigate('/practice/recommended')} style={{ border: 0, background: 'none', color: '#14593C', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                        Keep going →
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: wc.focusTag ? 8 : 0 }}>
                    <span style={{ fontSize: 13, color: '#6B7A6F', fontWeight: 600, minWidth: 170 }}>
                      Practise {wc.target} questions
                    </span>
                    {bar(wc.done, wc.target, '#5DAA3A')}
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#14593C', minWidth: 48, textAlign: 'right' }}>{wc.done}/{wc.target}</span>
                  </div>
                  {wc.focusTag && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 13, color: '#6B7A6F', fontWeight: 600, minWidth: 170 }}>
                        {wc.focusLabel}: {wc.focusTarget} questions
                      </span>
                      {bar(wc.focusDone, wc.focusTarget, '#9A6215')}
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#9A6215', minWidth: 48, textAlign: 'right' }}>{wc.focusDone}/{wc.focusTarget}</span>
                    </div>
                  )}
                </section>
              );
            })()}

            {/* Class leaderboard — only when opted in and classmates share (ROO-172 C) */}
            {!previewMode && leaderboard?.optedIn && leaderboard.rows.length > 1 && (
              <section style={{ background: '#fff', border: '1.5px solid #EFE6D0', borderRadius: 16, padding: '18px 20px', marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1A2A1F' }}>
                    Class leaderboard{leaderboard.className ? ` · ${leaderboard.className}` : ''}
                  </h2>
                  <span style={{ fontSize: 12.5, color: '#6B7A6F', fontWeight: 600 }}>questions this week</span>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {leaderboard.rows.slice(0, 5).map((r) => (
                    <li key={r.rank} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: r.rank > 1 ? '1px solid #F3EEE0' : 'none', fontWeight: r.isMe ? 800 : 600, color: r.isMe ? '#14593C' : '#1A2A1F', fontSize: 13.5 }}>
                      <span style={{ width: 26 }}>{r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : `${r.rank}.`}</span>
                      <span style={{ flex: 1 }}>{r.name}{r.isMe ? ' (you)' : ''}</span>
                      <span>{r.answered}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {!previewMode && recommended && recommended.band && recommended.tests.length > 0 && (
              <RecommendedPractice
                data={recommended}
                onStart={(t) => handleStartTest(t.id, t.title)}
              />
            )}
    </>
  );

  // Account dropdown items, shared by the desktop nav and the mobile top bar.
  const userMenuItems: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    danger?: boolean;
    onClick: () => void;
  }> = [
    { key: 'profile', label: 'Profile', icon: Icon.User, onClick: () => { setUserMenuOpen(false); navigate('/profile'); } },
    { key: 'settings', label: 'Settings', icon: Icon.Gear, onClick: () => { setUserMenuOpen(false); navigate('/settings'); } },
    { key: 'logout', label: 'Log out', icon: Icon.Logout, danger: true, onClick: () => { setUserMenuOpen(false); logout(); } },
  ];
  const userMenuDropdown = (
    <div className="cvr-usermenu__dropdown" role="menu">
      {userMenuItems.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={`cvr-usermenu__item${item.danger ? ' is-danger' : ''}`}
          onClick={item.onClick}
        >
          <span className="cvr-usermenu__icon">{item.icon}</span>
          {item.label}
        </button>
      ))}
    </div>
  );

  // ────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────
  return (
    <div className="cvr-portal">
      {/* DESKTOP NAV */}
      <header className="cvr-nav">
        <div className="cvr-nav__inner">
          <button className="cvr-nav__brand" type="button">
            <img src={logoUrl} alt="Cleveroo" />
            <span>cleve<span className="r">r</span><span className="o">oo</span></span>
          </button>
          <nav className="cvr-nav__links">
            <button
              type="button"
              className={view === 'dashboard' ? 'is-active' : ''}
              onClick={() => setView('dashboard')}
            >
              {Icon.Home}Dashboard
            </button>
            <button
              type="button"
              className={view === 'resources' ? 'is-active' : ''}
              onClick={() => setView('resources')}
            >
              {Icon.Resources}Resources
            </button>
          </nav>
          <div className="cvr-nav__right">
            <span className="cvr-nav__streak">
              {Icon.Flame}
              {vm.streakDays || 0} day streak
            </span>
            {/* Effort-level badge; keeps the points visible so the chip doesn't
                appear to silently "switch metric" once engagement loads (ROO-220). */}
            {engagement?.level ? (
              <span className="cvr-nav__points" title={`${engagement.level.answered} questions answered`}>
                {engagement.level.icon} {engagement.level.name}
                {vm.pointsTotal ? ` · ${vm.pointsTotal.toLocaleString()} pts` : ''}
              </span>
            ) : (
              <span className="cvr-nav__points">
                {Icon.Star}
                {(vm.pointsTotal || 0).toLocaleString()} pts
              </span>
            )}
            <div className="cvr-usermenu" ref={userMenuRef}>
              <button
                className={`cvr-nav__user${userMenuOpen ? ' is-open' : ''}`}
                type="button"
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                onClick={() => setUserMenuOpen((o) => !o)}
              >
                <span className="cvr-nav__avatar">{avatarNode}</span>
                {firstName}{(vm as any).lastInitial ? ` ${(vm as any).lastInitial}` : ''}
                {Icon.Chevron}
              </button>
              {userMenuOpen && userMenuDropdown}
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE TOP BAR */}
      <header className="cvr-top">
        <div className="cvr-top__brand">
          <img src={logoUrl} alt="" />
          <span>cleve<span className="r">r</span><span className="o">oo</span></span>
        </div>
        <div className="cvr-top__right">
          {!isDayOne && (
            <span className="cvr-top__streak">
              {Icon.FlameSmall}
              {vm.streakDays || 0}
            </span>
          )}
          <div className="cvr-usermenu cvr-usermenu--mobile">
            <button
              className="cvr-top__avatar"
              type="button"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label="Account menu"
              onClick={() => setUserMenuOpen((o) => !o)}
            >
              {avatarNode}
            </button>
            {userMenuOpen && userMenuDropdown}
          </div>
        </div>
      </header>

      <main className="cvr-wrap cvr-page">
        {/* Honest degradation (ROO-215): if a core fetch failed we show empty
            sections, so tell the student it's a loading problem — not zero
            progress. */}
        {fetchFailed && !previewMode && (
          <div
            role="alert"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#FFF4E0', border: '1.5px solid #F2C87E',
              borderRadius: 14, padding: '10px 16px', marginBottom: 16,
              color: '#7A4E12', fontSize: 13.5, fontWeight: 600,
            }}
          >
            <span aria-hidden="true">⚠️</span>
            Some of your info couldn&rsquo;t load just now, so parts of this page may look empty.
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginLeft: 'auto', border: '1.5px solid #E5B96A', background: '#fff',
                color: '#7A4E12', borderRadius: 999, padding: '5px 14px',
                fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Try again
            </button>
          </div>
        )}
        {view === 'progress' && currentUser?.id ? (
          <ProgressView
            studentId={currentUser.id}
            onPractiseSkill={(skill) => navigate(`/practice/${encodeURIComponent(skill)}`)}
          />
        ) : view === 'achievements' ? (
          renderAchievementsCollection({ achievements: vm.achievements, onBack: () => navigate('/') })
        ) : view === 'resources' ? (
          articleSlug && currentUser?.id ? (
            <ArticleView
              studentId={currentUser.id}
              slug={articleSlug}
              onBack={() => navigate('/resources')}
            />
          ) : (
            renderResources({
              resources: resourcesData,
              onOpenArticle: (slug: string) => navigate(`/resources/${slug}`),
              query: searchParams.get('q') || '',
              category: searchParams.get('cat') || '',
              onFilterChange: (next: { query?: string; category?: string }) => {
                const p = new URLSearchParams(searchParams);
                if (next.query !== undefined) { next.query ? p.set('q', next.query) : p.delete('q'); }
                if (next.category !== undefined) { next.category ? p.set('cat', next.category) : p.delete('cat'); }
                setSearchParams(p, { replace: true });
              },
            })
          )
        ) : (
          <>
            {isDayOne ? renderDayOne({
              vm,
              firstName,
              onPlacementCta: handlePlacementCta,
              onViewReport: () => navigate('/report'),
              // navigate('/') from the dashboard was a no-op dead link (ROO-186)
              onPractise: () => navigate('/practice/recommended'),
              onSubjectTile: handleSubjectTile,
              onGoalPick: () => setShowGoalPicker(true),
              onNavigate: (p: string) => navigate(p),
              postHeroCards,
              placementCompleted,
              weeklyGoalMinutes,
            }) : renderActive({
              vm,
              onQuestStep: handleQuestStep,
              onSubjectTile: handleSubjectTile,
              onPendingClick: (p) => handleStartTest(p.id, p.title),
              onContinueQuest: startFirstIncompleteQuestStep,
              onSwitchSubject: () => navigate('/'),
              onNavigate: (p: string) => navigate(p),
              onMakeupDay: handleMakeupDay,
              postHeroCards,
              questSectionRef,
            })}
          </>
        )}
      </main>

      {/* MOBILE BOTTOM TAB BAR — portaled to body so position:fixed
          isn't broken by any transform/contain context in ancestors. */}
      {createPortal(
        <nav className="cvr-tabbar">
          <button
            className={`cvr-tab${view === 'dashboard' ? ' is-active' : ''}`}
            type="button"
            onClick={() => setView('dashboard')}
          >
            {Icon.TabHome}Home
          </button>
          <button
            className="cvr-tab cvr-tab--quest"
            type="button"
            onClick={() => {
              setView('dashboard');
              // scroll to quest section after state update
              setTimeout(() => {
                questSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
          >
            {Icon.TabQuest}Quest
          </button>
          <button
            className="cvr-tab"
            type="button"
            onClick={() => navigate('/profile')}
          >
            {Icon.TabMe}Me
          </button>
        </nav>,
        document.body
      )}

      {/* Streak-makeup confirm dialog (ROO-221) */}
      {makeupDialog}

      {/* Weekly goal picker modal — rendered via portal so it floats above everything */}
      {showGoalPicker && currentUser?.id && createPortal(
        <WeeklyGoalPicker
          studentId={currentUser.id}
          onSaved={handleGoalSaved}
          onCancel={() => setShowGoalPicker(false)}
        />,
        document.body
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Resources view (sub-render)
// ────────────────────────────────────────────────────────────────────────────
const resourceTypeLabel = (type: string): string => {
  const t = type.toLowerCase();
  if (t === 'video') return 'Video';
  if (t === 'article' || t === 'doc') return 'Article';
  if (t === 'worksheet') return 'Worksheet';
  if (t === 'quiz') return 'Quiz';
  return type;
};

const resourceTypeColor = (type: string): string => {
  const t = type.toLowerCase();
  if (t === 'video') return 'var(--coral)';
  if (t === 'article' || t === 'doc') return 'var(--grape)';
  if (t === 'worksheet') return 'var(--orange)';
  return 'var(--green)';
};

const ResourceCard: React.FC<{ r: ResourceVM; onOpenArticle: (slug: string) => void }> = ({ r, onOpenArticle }) => {
  const isArt = !!(r.isArticle && r.slug);
  const inner = (
    <>
      {isArt && articleHasArt(r.slug) && (
        <img
          src={articleImage(r.slug)!}
          alt=""
          loading="lazy"
          style={{ height: 130, width: 'calc(100% + 32px)', margin: '-14px -16px 12px', objectFit: 'cover', display: 'block' }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
          color: resourceTypeColor(r.type), background: resourceTypeColor(r.type) + '22',
          borderRadius: 6, padding: '2px 7px',
        }}>
          {resourceTypeLabel(r.type)}
        </span>
        {r.readMinutes ? (
          <span style={{ fontSize: 11, color: '#6b7280' }}>{r.readMinutes} min read</span>
        ) : r.subject ? (
          <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'capitalize' }}>
            {r.subject.replace(/_/g, ' ')}
          </span>
        ) : null}
      </div>
      <div style={{ fontWeight: 700, fontSize: 14.5, color: 'var(--ink)', marginBottom: 4 }}>{r.title}</div>
      {r.description && (
        <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.45 }}>{r.description}</div>
      )}
    </>
  );
  const cardStyle: React.CSSProperties = {
    display: 'block', textAlign: 'left', width: '100%', textDecoration: 'none',
    border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '14px 16px',
    background: '#fff', cursor: 'pointer', transition: 'box-shadow 0.15s',
    overflow: 'hidden',
  };
  // In-app articles open the reader route; anything else is a real external link.
  if (r.isArticle && r.slug) {
    return (
      <button type="button" onClick={() => onOpenArticle(r.slug!)} style={cardStyle}>{inner}</button>
    );
  }
  return (
    <a href={r.url} target="_blank" rel="noopener noreferrer" style={cardStyle}>{inner}</a>
  );
};

const renderAchievementsCollection = (
  { achievements, onBack }: { achievements: AchievementVM[]; onBack: () => void },
) => {
  // Earned first, then closest in-progress; the dashboard shows a 4×4 slice of this.
  const ordered = [...achievements].sort((a, b) => Number(b.unlocked) - Number(a.unlocked));
  const unlocked = ordered.filter((a) => a.unlocked).length;
  return (
    <section style={{ width: '100%', maxWidth: 960, margin: '0 auto', paddingTop: 24 }}>
      <button
        type="button"
        onClick={onBack}
        style={{
          border: '1.5px solid var(--line-strong)', background: '#fff', color: 'var(--ink)',
          borderRadius: 999, padding: '6px 16px', fontSize: 13, fontWeight: 700,
          cursor: 'pointer', marginBottom: 16,
        }}
      >
        ← Back to dashboard
      </button>
      <div className="cvr-card" style={{ marginBottom: 18 }}>
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--yellow-soft)', color: 'var(--anchor)' }}>{Icon.Bolt}</div>
          <h2>Achievements</h2>
        </div>
        <p style={{ margin: '2px 0 0', color: 'var(--mute)', fontSize: 14 }}>
          <strong style={{ color: 'var(--ink)' }}>{unlocked}</strong> of {ordered.length} unlocked — keep going to collect them all!
        </p>
      </div>
      <div className="cvr-ach-grid--all">
        {ordered.map((a) => (
          <div
            key={a.id}
            className={`cvr-ach${a.unlocked ? ' is-unlocked' : ' is-locked'}`}
            data-tone={a.unlocked ? a.tone : undefined}
          >
            {!a.unlocked && <div className="cvr-ach__lock">🔒</div>}
            {badgeImage(a.id) ? (
              <div className="cvr-ach__ico cvr-ach__ico--img">
                <img className="cvr-ach__img" src={badgeImage(a.id)!} alt="" />
              </div>
            ) : (
              <div className="cvr-ach__ico">{a.glyph}</div>
            )}
            <div className="cvr-ach__name">{a.name}</div>
            <div className="cvr-ach__prog">{a.prog}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const renderResources = (
  { resources, onOpenArticle, query, category, onFilterChange }: {
    resources: ResourceVM[] | null;
    onOpenArticle: (slug: string) => void;
    query: string;
    category: string;
    onFilterChange: (next: { query?: string; category?: string }) => void;
  },
) => {
  const all = resources || [];
  // Distinct categories in first-appearance order for the filter chips.
  const categories: string[] = [];
  all.forEach((r) => { const c = r.category || 'More resources'; if (!categories.includes(c)) categories.push(c); });

  // Apply the category + text filters.
  const q = query.trim().toLowerCase();
  const filtered = all.filter((r) => {
    const cat = r.category || 'More resources';
    if (category && cat !== category) return false;
    if (q && !`${r.title} ${r.description || ''} ${cat}`.toLowerCase().includes(q)) return false;
    return true;
  });

  // Group the filtered set by category (falls back to "More" for legacy rows).
  const groups = new Map<string, ResourceVM[]>();
  filtered.forEach((r) => {
    const key = r.category || 'More resources';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  });

  const isFiltering = !!q || !!category;
  const chip = (active: boolean): React.CSSProperties => ({
    border: `1.5px solid ${active ? 'var(--ink)' : 'var(--line-strong)'}`,
    background: active ? 'var(--ink)' : '#fff',
    color: active ? '#fff' : 'var(--ink)',
    borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', whiteSpace: 'nowrap',
  });

  return (
    <section style={{ maxWidth: 960, margin: '0 auto', paddingTop: 24 }}>
      <div className="cvr-card" style={{ marginBottom: 18 }}>
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--grape-soft)', color: 'var(--grape)' }}>
            {Icon.Resources}
          </div>
          <h2>Learning Resources</h2>
        </div>
        <p style={{ margin: '2px 0 12px', color: 'var(--mute)', fontSize: 14 }}>
          Short, practical guides for every part of the test — strategies you can use today.
        </p>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--mute)', display: 'inline-flex', pointerEvents: 'none',
          }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
            </svg>
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => onFilterChange({ query: e.target.value })}
            placeholder={`Search ${all.length} guides…`}
            aria-label="Search resources"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 36px 10px 36px',
              border: '1.5px solid var(--line-strong)', borderRadius: 12, fontSize: 14.5,
              color: 'var(--ink)', background: 'var(--paper)', outline: 'none', fontFamily: 'inherit',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => onFilterChange({ query: '' })}
              aria-label="Clear search"
              style={{
                position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                border: 'none', background: 'transparent', color: 'var(--mute)', cursor: 'pointer',
                fontSize: 18, lineHeight: 1, padding: 4,
              }}
            >×</button>
          )}
        </div>

        {/* Category filter */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={chip(!category)} onClick={() => onFilterChange({ category: '' })}>
            All
          </button>
          {categories.map((c) => (
            <button key={c} type="button" style={chip(category === c)}
              onClick={() => onFilterChange({ category: category === c ? '' : c })}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {!resources || resources.length === 0 ? (
        <div className="cvr-card">
          <div className="cvr-empty">
            <div className="cvr-empty__ico">{Icon.Doc}</div>
            <div className="cvr-empty__title">No resources available yet</div>
            <div className="cvr-empty__sub">Check back soon — new guides are on the way.</div>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="cvr-card">
          <div className="cvr-empty">
            <div className="cvr-empty__ico">{Icon.Doc}</div>
            <div className="cvr-empty__title">No guides match your search</div>
            <div className="cvr-empty__sub">
              Try a different word or{' '}
              <button type="button" onClick={() => onFilterChange({ query: '', category: '' })}
                style={{ border: 'none', background: 'none', color: 'var(--grape)', fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                clear the filters
              </button>.
            </div>
          </div>
        </div>
      ) : (
        <>
          {isFiltering && (
            <p style={{ margin: '0 0 12px 2px', color: 'var(--mute)', fontSize: 13 }}>
              {filtered.length} {filtered.length === 1 ? 'guide' : 'guides'}
              {category ? ` in ${category}` : ''}{q ? ` matching “${query.trim()}”` : ''}
            </p>
          )}
          {Array.from(groups.entries()).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom: 22 }}>
              <h3 style={{ fontSize: 17, color: 'var(--ink)', margin: '0 0 10px' }}>{cat}</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: 12,
              }}>
                {items.map((r) => (
                  <ResourceCard key={r.id} r={r} onOpenArticle={onOpenArticle} />
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </section>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Active view (sub-render)
// ────────────────────────────────────────────────────────────────────────────
interface ActiveViewProps {
  vm: typeof MOCK_ACTIVE;
  onQuestStep: (id: string) => void;
  onSubjectTile: (id: string) => void;
  onPendingClick: (p: PendingVM) => void;
  onContinueQuest: () => void;
  onSwitchSubject: () => void;
  onNavigate: (path: string) => void;
  /** Tap a missed streak day to launch the makeup quiz (ROO-221). */
  onMakeupDay?: (date: string) => void;
  postHeroCards?: React.ReactNode;
  questSectionRef?: React.RefObject<HTMLDivElement | null>;
}
const renderActive = ({ vm, onQuestStep, onSubjectTile, onPendingClick, onContinueQuest, onSwitchSubject, onNavigate, onMakeupDay, postHeroCards, questSectionRef }: ActiveViewProps) => (
  <>
    {/* DESKTOP WELCOME HERO (hidden on mobile via CSS) */}
    <section className="cvr-welcome cvr-welcome--desktop">
      <div className="cvr-welcome__inner">
        <div className="cvr-welcome__mascot">
          <img src={logoUrl} alt="Mascot" />
        </div>
        <div className="cvr-welcome__copy">
          <h1 className="cvr-welcome__greeting">
            G&rsquo;day, {vm.firstName}! <span className="cvr-wave">👋</span>
          </h1>
          <p className="cvr-welcome__sub">{vm.greetingSub}</p>
          <div className="cvr-welcome__chips">
            <span className="cvr-chip">{Icon.Lines}{vm.yearLabel}</span>
            {vm.levelLabel && <span className="cvr-chip">{Icon.ChipStar}{vm.levelLabel}</span>}
            {vm.topChip && <span className="cvr-chip cvr-chip--yellow">{vm.topChip}</span>}
          </div>
        </div>
        <div className="cvr-welcome__streak">
          <div className="cvr-welcome__streak__num">{vm.streakDays}</div>
          <div className="cvr-welcome__streak__lbl">day streak — keep going!</div>
          <div className="cvr-welcome__streak__week">
            {vm.weeklyHabit.map((d, i) =>
              d.status === 'missed' && d.date ? (
                <button
                  key={i}
                  type="button"
                  className="is-missed"
                  title="Make up this day with a quick quiz"
                  onClick={() => onMakeupDay?.(d.date)}
                >
                  +
                </button>
              ) : (
                <span
                  key={i}
                  className={d.status === 'done' ? 'is-done' : d.status === 'madeup' ? 'is-madeup' : d.status === 'today' ? 'is-today' : ''}
                  title={d.status === 'madeup' ? 'Made up with a quiz' : undefined}
                >
                  {d.day}
                </span>
              ),
            )}
          </div>
        </div>
      </div>
    </section>

    {/* MOBILE WELCOME HERO (display swap via CSS) */}
    <section className="cvr-hero-mobile">
      <div className="cvr-hero-mobile__row">
        <div className="cvr-hero-mobile__mascot">
          <img src={logoUrl} alt="Mascot" />
        </div>
        <div className="cvr-hero-mobile__copy">
          <h1 className="cvr-hero-mobile__title">
            G&rsquo;day, {vm.firstName}! <span className="cvr-wave">👋</span>
          </h1>
          <p className="cvr-hero-mobile__sub">{vm.greetingSub}</p>
        </div>
      </div>
      <div className="cvr-hero-mobile__streak">
        <div className="cvr-hero-mobile__streak__col">
          <div className="cvr-hero-mobile__streak__num">{vm.streakDays}</div>
          <div className="cvr-hero-mobile__streak__lbl">day streak</div>
        </div>
        <div className="cvr-hero-mobile__streak__week">
          {vm.weeklyHabit.map((d, i) =>
            d.status === 'missed' && d.date ? (
              <button key={i} type="button" className="is-missed" title="Make up this day" onClick={() => onMakeupDay?.(d.date)}>+</button>
            ) : (
              <span key={i} className={d.status === 'done' ? 'is-done' : d.status === 'madeup' ? 'is-madeup' : d.status === 'today' ? 'is-today' : ''}>{d.day}</span>
            ),
          )}
        </div>
        <div className="cvr-hero-mobile__streak__col cvr-hero-mobile__streak__col--right">
          <div className="cvr-hero-mobile__streak__points">{(vm.pointsTotal || 0).toLocaleString()}</div>
          <div className="cvr-hero-mobile__streak__points-lbl">POINTS</div>
        </div>
      </div>
    </section>

    {/* Focus/challenge/recommended stack lives under the hero (ROO-177) */}
    {postHeroCards}

    {/* ROW 1: TODAY'S QUEST + PENDING
        On mobile, the subject-progress h-scroll rail (cvr-mobile-rail--progress)
        sits between Quest and Pending via CSS order. */}
    <section className="cvr-row-today" ref={questSectionRef}>
      <div className="cvr-quest cvr-mobile-order-1">
        <div className="cvr-quest__kicker">Today&rsquo;s quest · 15 min</div>
        <h2 className="cvr-quest__title">{vm.questTitle}</h2>
        <p className="cvr-quest__sub">{vm.questSub}</p>
        {vm.questSteps.length === 0 ? (
          // No quest loaded (not fetched yet or the fetch failed) — show an
          // honest line instead of MOCK steps + a dead "Continue quest" CTA
          // that no-ops because questData is null (ROO-191).
          <div className="cvr-quest__steps">
            <p className="cvr-quest__sub" style={{ opacity: 0.85 }}>
              Today&rsquo;s quest isn&rsquo;t ready yet — check back in a moment.
            </p>
          </div>
        ) : (
          <>
            <div className="cvr-quest__steps">
              {vm.questSteps.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  className={`cvr-qstep${s.done ? ' is-done' : ''}`}
                  data-tone={i === 0 ? 'g' : i === 1 ? 'c' : 'p'}
                  onClick={() => onQuestStep(s.id)}
                >
                  <div className="cvr-qstep__num">{s.done ? '' : i + 1}</div>
                  <div className="cvr-qstep__body">
                    <div className="cvr-qstep__title">{s.title}</div>
                    <div className="cvr-qstep__meta">{s.meta}</div>
                  </div>
                  <span className="cvr-qstep__time">{s.time}</span>
                </button>
              ))}
            </div>
            <div className="cvr-quest__foot">
              <button className="cvr-btn-primary cvr-btn-primary--orange" type="button" onClick={onContinueQuest}>
                Continue quest{Icon.Arrow}
              </button>
            </div>
            <button className="cvr-quest__cta-mobile" type="button" onClick={onContinueQuest}>
              Continue quest{Icon.Arrow}
            </button>
          </>
        )}
      </div>

      {/* MOBILE-ONLY: subject progress h-scroll rail (between quest & pending) */}
      <div className="cvr-mobile-rail cvr-mobile-rail--progress cvr-mobile-order-2">
        <div className="cvr-section__head">
          <h2>Subject progress</h2>
          <button className="cvr-link" type="button" onClick={() => onNavigate('/progress')}>Report →</button>
        </div>
        <div className="cvr-stat-scroll">
          {vm.progress.map((row) => (
            <button key={`m-prog-${row.id}`} type="button" className="cvr-stat-tile">
              <div className={`cvr-stat-tile__ico cvr-stat-tile__ico--${row.tone}`}>{subjectIconSm(row.tone)}</div>
              <div className="cvr-stat-tile__name">{row.name.split(' ')[0]}</div>
              <div className="cvr-stat-tile__num">{row.pct}<small>%</small></div>
              <div className="cvr-stat-tile__bar" style={{ ['--p' as any]: `${row.pct}%`, ['--c' as any]: cssVar(row.tone) }} />
              <div className="cvr-stat-tile__delta" style={row.delta.startsWith('+') ? undefined : { color: 'var(--mute)' }}>
                {row.delta.startsWith('+') ? `↑ ${row.delta} this week` : row.delta}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="cvr-card cvr-pending cvr-mobile-order-3">
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>{Icon.Doc}</div>
          <h2>Pending tests</h2>
          <span className="cvr-badge">{vm.pending.length} to do</span>
        </div>
        {vm.pending.length === 0 ? (
          <div className="cvr-empty">
            <div className="cvr-empty__ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.ClipCheck}</div>
            <div className="cvr-empty__title">You&rsquo;re all caught up!</div>
            <div className="cvr-empty__sub">When your teacher assigns a test it&rsquo;ll land right here.</div>
          </div>
        ) : (
          <div className="cvr-pending__list">
            {vm.pending.map((p) => (
              <button
                key={p.id}
                type="button"
                className="cvr-ptest"
                onClick={() => onPendingClick(p)}
              >
                <div className={`cvr-ptest__ico${p.tone === 'grape' ? ' cvr-ptest__ico--grape' : p.tone === 'green' ? ' cvr-ptest__ico--green' : p.tone === 'orange' ? ' cvr-ptest__ico--orange' : ''}`}>{p.letter}</div>
                <div>
                  <div className="cvr-ptest__title">{p.title}</div>
                  <div className="cvr-ptest__meta">{p.meta}</div>
                </div>
                <span className={`cvr-ptest__due${p.dueVariant === 'soon' ? ' cvr-ptest__due--soon' : p.dueVariant === 'later' ? ' cvr-ptest__due--later' : ''}`}>{p.due}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>

    {/* ROW 2: SUBJECT PROGRESS + JUMP BACK IN + THIS WEEK */}
    <section className="cvr-row-progress">
      <div className="cvr-card">
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.Trend}</div>
          <h2>Subject progress</h2>
          <button className="cvr-link" type="button" onClick={() => onNavigate('/progress')}>View report →</button>
        </div>
        {vm.progress.length === 0 ? (
          <div className="cvr-empty">
            <div className="cvr-empty__ico">{Icon.EmptyTrend}</div>
            <div className="cvr-empty__title">No data yet</div>
            <div className="cvr-empty__sub">Finish a few practice sets and your mastery will show here.</div>
          </div>
        ) : (
          <div className="cvr-prog__list">
            {vm.progress.map((row) => (
              <div className="cvr-prog__row" key={row.id}>
                <div className={`cvr-prog__ico cvr-prog__ico--${row.tone}`}>{subjectIcon(row.tone)}</div>
                <div>
                  <div className="cvr-prog__name">{row.name}</div>
                  <div className="cvr-prog__delta">
                    {row.delta.startsWith('+') ? <><b>{row.delta}</b> this week</> : row.delta}
                  </div>
                </div>
                <div className="cvr-prog__num">{row.pct}%</div>
                <div className="cvr-prog__bar" style={{ ['--p' as any]: `${row.pct}%`, ['--c' as any]: cssVar(row.tone) }} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cvr-card">
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--orange-soft)', color: 'var(--orange)' }}>{Icon.Bolt}</div>
          <h2>Jump back in</h2>
        </div>
        <div className="cvr-qa-grid">
          {vm.qa.map((t) => (
            <button key={t.id} type="button" className="cvr-qa-tile" onClick={() => onSubjectTile(t.id)}>
              <div className={`cvr-qa-tile__ico cvr-qa-tile__ico--${t.tone}`}>{qaIcon(t.tone)}</div>
              <div>
                <div className="cvr-qa-tile__name">{t.name}</div>
              </div>
              <div className="cvr-qa-tile__meta">
                <span><span className="cvr-qa-tile__count">{t.count}</span> {t.unit}</span>
                <span className="cvr-qa-tile__arrow">→</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="cvr-card">
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--grape-soft)', color: 'var(--grape)' }}>{Icon.Calendar}</div>
          <h2>This week</h2>
        </div>
        {vm.upcoming.length === 0 && (
          <div className="cvr-empty">
            <div className="cvr-empty__ico" style={{ background: 'var(--grape-soft)', color: 'var(--grape)' }}>{Icon.Calendar}</div>
            <div className="cvr-empty__title">No classes this week</div>
            <div className="cvr-empty__sub">When you join a class, your live sessions will show up here.</div>
          </div>
        )}
        <div className="cvr-upcoming__list">
          {vm.upcoming.map((u) => (
            <button
              key={u.id}
              type="button"
              className="cvr-uclass"
              data-tone={String(u.tone)}
              onClick={u.joinUrl ? () => window.open(u.joinUrl!, '_blank', 'noopener,noreferrer') : undefined}
            >
              <div className="cvr-uclass__date">
                <b>{u.day}</b>
                <small>{u.weekday}</small>
              </div>
              <div>
                <div className="cvr-uclass__title">{u.title}</div>
                <div className="cvr-uclass__meta">{u.meta}</div>
              </div>
              <div className="cvr-uclass__teacher">{u.initials}</div>
            </button>
          ))}
        </div>
      </div>
    </section>

    {/* ROW 3: ACHIEVEMENTS + ACTIVITY (teacher feedback removed, ROO-181) */}
    <section className="cvr-row-bottom">
      <div className="cvr-card cvr-card--ach-desktop cvr-mobile-order-1">
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--yellow-soft)', color: 'var(--orange)' }}>{Icon.Trophy}</div>
          <h2>Achievements</h2>
          <button className="cvr-link" type="button" onClick={() => onNavigate('/achievements')}>View all →</button>
        </div>
        <div className="cvr-ach-grid">
          {/* Dashboard shows a compact 4×4: earned badges first, then the
              closest in-progress ones. Full collection lives behind "View all". */}
          {[...vm.achievements]
            .sort((a, b) => Number(b.unlocked) - Number(a.unlocked))
            .slice(0, 16)
            .map((a) => (
            <div
              key={a.id}
              className={`cvr-ach${a.unlocked ? ' is-unlocked' : ' is-locked'}`}
              data-tone={a.unlocked ? a.tone : undefined}
            >
              {!a.unlocked && <div className="cvr-ach__lock">🔒</div>}
              {badgeImage(a.id) ? (
                <div className="cvr-ach__ico cvr-ach__ico--img">
                  <img className="cvr-ach__img" src={badgeImage(a.id)!} alt="" />
                </div>
              ) : (
                <div className="cvr-ach__ico">{a.glyph}</div>
              )}
              <div className="cvr-ach__name">{a.name}</div>
              <div className="cvr-ach__prog">{a.prog}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cvr-card cvr-card--activity-desktop cvr-mobile-order-1">
        <div className="cvr-card__head">
          <div className="cvr-ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.Clock}</div>
          <h2>Recent activity</h2>
          <button className="cvr-link" type="button" onClick={() => onNavigate('/progress')}>History →</button>
        </div>
        {vm.activity.length === 0 ? (
          <div className="cvr-empty">
            <div className="cvr-empty__ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.Clock}</div>
            <div className="cvr-empty__title">No activity yet</div>
            <div className="cvr-empty__sub">Finish a quick warm-up and your runs will show up here.</div>
          </div>
        ) : (
          <div className="cvr-activity__list">
            {vm.activity.map((a) => (
              <div key={a.id} className="cvr-act" data-tone={a.tone}>
                <div>
                  <div className="cvr-act__text">{a.text}</div>
                  <div className="cvr-act__sub">{a.sub}</div>
                </div>
                {a.score ? (
                  <span className={`cvr-act__score${a.scoreVariant === 'coral' ? ' cvr-act__score--coral' : a.scoreVariant === 'orange' ? ' cvr-act__score--orange' : ''}`}>
                    {a.score}
                  </span>
                ) : a.timeOnly ? (
                  <span className="cvr-act__time">{a.timeOnly}</span>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MOBILE-ONLY: achievements h-scroll rail */}
      <div className="cvr-mobile-rail cvr-mobile-rail--ach cvr-mobile-order-2">
        <div className="cvr-section__head">
          <h2>Achievements</h2>
          <button className="cvr-link" type="button" onClick={() => onNavigate('/achievements')}>View all →</button>
        </div>
        <div className="cvr-ach-scroll">
          {vm.achievements.map((a) => (
            <div
              key={`m-${a.id}`}
              className={`cvr-ach-mobile${a.unlocked ? ' is-unlocked' : ' is-locked'}`}
              data-tone={a.unlocked ? a.tone : undefined}
            >
              {!a.unlocked && <div className="cvr-ach-mobile__lock">🔒</div>}
              <div className="cvr-ach-mobile__ico">{a.glyph}</div>
              <div className="cvr-ach-mobile__name">{a.name}</div>
              <div className="cvr-ach-mobile__prog">{a.prog}</div>
            </div>
          ))}
        </div>
      </div>

    </section>
  </>
);

// ────────────────────────────────────────────────────────────────────────────
// Day-one view (sub-render)
// ────────────────────────────────────────────────────────────────────────────
interface DayOneViewProps {
  vm: typeof MOCK_ACTIVE;
  firstName: string;
  onPlacementCta: () => void;
  onViewReport: () => void;
  onPractise: () => void;
  onSubjectTile: (id: string) => void;
  onGoalPick: () => void;
  onNavigate: (path: string) => void;
  postHeroCards?: React.ReactNode;
  placementCompleted: boolean;
  weeklyGoalMinutes: number | null;
}
const renderDayOne = ({
  vm,
  firstName,
  onPlacementCta,
  onViewReport,
  onPractise,
  onSubjectTile,
  onGoalPick,
  onNavigate,
  postHeroCards,
  placementCompleted,
  weeklyGoalMinutes,
}: DayOneViewProps) => {
  // Progress ring: count how many of the 3 steps are done.
  // Step 1 (profile) is always done.
  const stepsDone = 1 + (placementCompleted ? 1 : 0) + (weeklyGoalMinutes != null ? 1 : 0);
  const ringPct = Math.round((stepsDone / 3) * 100);
  const ringR = 18;
  const ringC = 2 * Math.PI * ringR; // ≈ 113
  const ringOffset = ringC * (1 - ringPct / 100);
  void firstName;

  return (
    <>
      {/* DAY-1 WELCOME (simpler) */}
      <section className="cvr-welcome">
        <div className="cvr-welcome__inner">
          <div className="cvr-welcome__mascot">
            <img src={logoUrl} alt="Mascot" />
          </div>
          <div className="cvr-welcome__copy">
            {/* Post-placement the hero becomes a compact welcome-back — the
                focus-areas card above is the single source of CTAs (ROO-172 D). */}
            <h1 className="cvr-welcome__greeting">
              {placementCompleted ? 'Welcome back' : 'Welcome'}, {vm.firstName}! <span className="cvr-wave">👋</span>
            </h1>
            <p className="cvr-welcome__sub">
              {placementCompleted
                ? 'Nice work on your placement — your plan for the week is ready below.'
                : 'Let’s set you up — three quick steps and you’re hopping.'}
            </p>
            {!placementCompleted && (
              <div className="cvr-welcome__chips">
                <button
                  type="button"
                  className="cvr-btn-primary cvr-btn-primary--orange"
                  onClick={onPlacementCta}
                  style={{ marginTop: 4 }}
                >
                  Start with a 5-minute placement{Icon.Arrow}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {postHeroCards}

      {/* SETUP CHECKLIST — single column once placement is done: the duplicate
          "placement is done 🎉" celebration card is retired (ROO-172 D) */}
      <section className="cvr-row-today" style={{ gridTemplateColumns: placementCompleted ? '1fr' : '1fr 1fr' }}>
        <div className="cvr-checklist">
          <div className="cvr-checklist__prog">
            <div className="cvr-checklist__prog__ring">
              <svg width="52" height="52" viewBox="0 0 44 44">
                <circle cx="22" cy="22" r={ringR} stroke="var(--cream)" strokeWidth="4" fill="none" />
                <circle
                  cx="22"
                  cy="22"
                  r={ringR}
                  stroke="var(--green)"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={ringC.toFixed(0)}
                  strokeDashoffset={ringOffset.toFixed(0)}
                  strokeLinecap="round"
                />
              </svg>
              <div className="cvr-checklist__prog__ring__num">{ringPct}%</div>
            </div>
            <div className="cvr-checklist__prog__lbl">
              <b>You&rsquo;re {stepsDone} step{stepsDone !== 1 ? 's' : ''} in</b>
              <small>
                {stepsDone < 3
                  ? `Finish ${3 - stepsDone} more to unlock your daily quest`
                  : 'All set — your quest is unlocked!'}
              </small>
            </div>
          </div>

          {/* Step 1: always done */}
          <div className="cvr-check is-done">
            <div className="cvr-check__box" />
            <div>
              <div className="cvr-check__title">Create your profile</div>
              <div className="cvr-check__meta">Year 4 · OC track</div>
            </div>
            <span className="cvr-check__cta">Done</span>
          </div>

          {/* Step 2: placement */}
          <div className={`cvr-check${placementCompleted ? ' is-done' : ''}`}>
            <div className="cvr-check__box" />
            <div>
              <div className="cvr-check__title">Take placement quiz</div>
              <div className="cvr-check__meta">5 min · we&rsquo;ll calibrate to you</div>
            </div>
            {placementCompleted
              ? <button className="cvr-check__cta" type="button" onClick={onViewReport}>View report</button>
              : <button className="cvr-check__cta" type="button" onClick={onPlacementCta}>Start</button>}
          </div>

          {/* Step 3: weekly goal */}
          <div className={`cvr-check${weeklyGoalMinutes != null ? ' is-done' : ''}`}>
            <div className="cvr-check__box" />
            <div>
              <div className="cvr-check__title">Pick your weekly goal</div>
              <div className="cvr-check__meta">
                {weeklyGoalMinutes != null
                  ? `${weeklyGoalMinutes} min / day`
                  : '15 min / day is a great start'}
              </div>
            </div>
            {weeklyGoalMinutes != null
              ? <span className="cvr-check__cta">Done</span>
              : <button className="cvr-check__cta" type="button" onClick={onGoalPick}>Pick</button>}
          </div>
        </div>

        {/* PLACEMENT CTA — pre-placement only; once done, the focus-areas card
            is the single "placement complete" surface (ROO-172 D) */}
        {placementCompleted ? null : (
          <div className="cvr-placement">
            <span className="cvr-placement__kicker">Recommended next</span>
            <h2 className="cvr-placement__title">Take the 5-min placement quiz.</h2>
            <p className="cvr-placement__sub">We figure out exactly where you&rsquo;re solid and what you nearly know — so your first quest fits like a glove.</p>
            <div className="cvr-placement__meta">
              <span className="cvr-placement__chip">⏱ 5 min</span>
              <span className="cvr-placement__chip">8 questions</span>
              <span className="cvr-placement__chip">No timer</span>
            </div>
            <button className="cvr-placement__cta" type="button" onClick={onPlacementCta}>
              Let&rsquo;s go{Icon.Arrow}
            </button>
          </div>
        )}
      </section>

      {/* SUBJECTS TO TRY */}
      <section>
        <div className="cvr-card">
          <div className="cvr-card__head">
            <div className="cvr-ico" style={{ background: 'var(--orange-soft)', color: 'var(--orange)' }}>{Icon.Bolt}</div>
            <h2>Subjects to try</h2>
          </div>
          <div className="cvr-try-grid">
            <button type="button" className="cvr-try-tile" onClick={() => onSubjectTile('reading')}>
              <div className="cvr-try-tile__ico cvr-try-tile__ico--green">{Icon.Reading}</div>
              <div className="cvr-try-tile__name">Reading</div>
              <div className="cvr-try-tile__meta"><b>12</b> tests ready</div>
            </button>
            <button type="button" className="cvr-try-tile" onClick={() => onSubjectTile('maths')}>
              <div className="cvr-try-tile__ico cvr-try-tile__ico--orange">{Icon.Maths}</div>
              <div className="cvr-try-tile__name">Maths</div>
              <div className="cvr-try-tile__meta"><b>13</b> sets</div>
            </button>
            <button type="button" className="cvr-try-tile" onClick={() => onSubjectTile('thinking')}>
              <div className="cvr-try-tile__ico cvr-try-tile__ico--grape">{Icon.ThinkingCheck}</div>
              <div className="cvr-try-tile__name">Thinking</div>
              <div className="cvr-try-tile__meta"><b>9</b> blocks</div>
            </button>
            <button type="button" className="cvr-try-tile" onClick={() => onSubjectTile('writing')}>
              <div className="cvr-try-tile__ico cvr-try-tile__ico--coral">{Icon.Writing}</div>
              <div className="cvr-try-tile__name">Writing</div>
              <div className="cvr-try-tile__meta"><b>6</b> prompts</div>
            </button>
          </div>
        </div>
      </section>

      {/* EMPTY ROW: progress + pending + parent tip */}
      <section className="cvr-row-progress">
        <div className="cvr-card">
          <div className="cvr-card__head">
            <div className="cvr-ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.Trend}</div>
            <h2>Subject progress</h2>
          </div>
          <div className="cvr-empty">
            <div className="cvr-empty__ico">{Icon.EmptyTrend}</div>
            <div className="cvr-empty__title">No data yet</div>
            <div className="cvr-empty__sub">Finish your placement quiz and we&rsquo;ll show your starting mastery in every subject.</div>
          </div>
        </div>

        <div className="cvr-card">
          <div className="cvr-card__head">
            <div className="cvr-ico" style={{ background: 'var(--coral-soft)', color: 'var(--coral)' }}>{Icon.Doc}</div>
            <h2>Pending tests</h2>
            <span className="cvr-badge">All clear</span>
          </div>
          <div className="cvr-empty">
            <div className="cvr-empty__ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.ClipCheck}</div>
            <div className="cvr-empty__title">You&rsquo;re all caught up!</div>
            <div className="cvr-empty__sub">When your teacher assigns a test it&rsquo;ll land right here.</div>
          </div>
        </div>

        <div className="cvr-tip">
          <div className="cvr-tip__ico">💡</div>
          <div>
            <div className="cvr-tip__title">Parent tip</div>
            <div className="cvr-tip__body">15 minutes a day works far better than two-hour cram sessions on weekends. We&rsquo;ll send your parent one weekly summary — no jargon.</div>
          </div>
        </div>
      </section>

      {/* EMPTY ROW: achievements + activity (feedback removed, ROO-181) */}
      <section className="cvr-row-bottom">
        <div className="cvr-card">
          <div className="cvr-card__head">
            <div className="cvr-ico" style={{ background: 'var(--yellow-soft)', color: 'var(--orange)' }}>{Icon.Trophy}</div>
            <h2>Achievements</h2>
            <button className="cvr-link" type="button" onClick={() => onNavigate('/progress')}>View all →</button>
          </div>
          <div className="cvr-empty">
            <div className="cvr-empty__ico" style={{ background: 'var(--yellow-soft)', color: 'var(--orange)' }}>{Icon.EmptyTrophy}</div>
            <div className="cvr-empty__title">First badge: just around the corner</div>
            <div className="cvr-empty__sub">
              {placementCompleted
                ? 'Practise to unlock your next badges — keep the streak going!'
                : 'Finish your placement to unlock “Welcome aboard”. 11 more after that.'}
            </div>
            <button className="cvr-empty__cta" type="button" onClick={placementCompleted ? onPractise : onPlacementCta}>
              {placementCompleted ? 'Practise now →' : 'Start placement →'}
            </button>
          </div>
        </div>

        <div className="cvr-card">
          <div className="cvr-card__head">
            <div className="cvr-ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.Clock}</div>
            <h2>Recent activity</h2>
          </div>
          <div className="cvr-empty">
            <div className="cvr-empty__ico" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}>{Icon.Clock}</div>
            <div className="cvr-empty__title">No activity yet</div>
            <div className="cvr-empty__sub">Once you start practising, your runs will show up here.</div>
          </div>
        </div>

      </section>
    </>
  );
};

export default StudentDashboard;
